// src/components/fundManagement/mainDashboard/MainDashboard.tsx
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import { useAppSelector } from 'app/hooks';
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { FaChartLine, FaCoins, FaWallet } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import {
  getUnitYearlyReturnStats,
  selectGrowthYearlyReturn,
  selectIncomeYearlyReturn,
  selectYearlyReturnLoading
} from 'slices/fundManagementInvestment/fundManagementConfigurationSlice';
import { getLandingData } from 'slices/fundManagementInvestment/fundmanagementInvestmentSlice';
import { clearBuySellingUnitsDashboardData } from 'slices/fundManagementInvestment/simulationDashboardSlice';
import AnalyticsPlaceholder from './AnalyticsPlaceholder';
import BuySellingUnitsSection from './BuySellingUnitsSection';

interface UnitData {
  unitType: string;
  NAV?: string | number;
  amountInvestedTillDate?: string | number;
  totalUnitsHoldTillDate?: string | number;
  [key: string]: any;
}

const blackGrayPalette = {
  background: 'linear-gradient(135deg, #0a0c0f 0%, #12151a 100%)',
  cardBg: 'rgba(20, 22, 28, 0.97)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  cardShadow: '0 10px 40px rgba(0, 0, 0, 0.7)',
  text: '#E2E8F0',
  subText: '#94A3B8',
  accent: '#60A5FA',
  glow: 'rgba(96, 165, 250, 0.3)'
};

const formatCurrency = (amount: number | string): string => {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return '$0';
  const abs = Math.abs(num);
  const sign = num < 0 ? '-' : '';
  if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(2)}`.replace(/\.00$/, '');
};

const StatCard = ({ icon, value, label, subLabel, index }: any) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: index * 0.08 }}
  >
    <Paper
      elevation={0}
      sx={{
        background: blackGrayPalette.cardBg,
        border: blackGrayPalette.border,
        boxShadow: blackGrayPalette.cardShadow,
        borderRadius: 2.5,
        p: { xs: 1.8, sm: 2.2 },
        height: '100%',
        backdropFilter: 'blur(8px)'
      }}
    >
      <Box display="flex" alignItems="center" gap={{ xs: 1.5, sm: 2 }}>
        <Box
          sx={{
            width: { xs: 32, sm: 36 },
            height: { xs: 32, sm: 36 },
            borderRadius: 2,
            background: 'linear-gradient(135deg, #3b82f6, #0ea5e9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          {React.cloneElement(icon, { size: 17, color: '#fff' })}
        </Box>
        <Box>
          <Typography
            sx={{
              color: blackGrayPalette.text,
              fontWeight: 700,
              fontSize: { xs: '0.95rem', sm: '1.05rem' },
              lineHeight: 1.1
            }}
          >
            {value}
          </Typography>
          <Typography
            sx={{
              color: blackGrayPalette.subText,
              fontSize: { xs: '0.7rem', sm: '0.75rem' },
              lineHeight: 1.2
            }}
          >
            {label}
          </Typography>
          {subLabel && (
            <Typography
              sx={{ color: '#64748B', fontSize: { xs: '0.65rem', sm: '0.7rem' }, fontWeight: 500 }}
            >
              {subLabel}
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  </motion.div>
);

/* ———————————————— PROFIT CIRCLE WITH INPUT + TOGGLE ———————————————— */
const ProfitCircle = () => {
  const dispatch = useDispatch<any>();
  const selectedProjectId = useAppSelector((s) => s.homeTilesServices.selectedProjectId);
  const [unitsHeld, setUnitsHeld] = useState('100');
  const [unitType, setUnitType] = useState<'growth' | 'income'>('growth');

  const growthStats = useAppSelector(selectGrowthYearlyReturn);
  const incomeStats = useAppSelector(selectIncomeYearlyReturn);
  const loading = useAppSelector(selectYearlyReturnLoading);

  const stats = unitType === 'growth' ? growthStats : incomeStats;

  useEffect(() => {
    const units = parseFloat(unitsHeld) || 100;
    if (selectedProjectId && units > 0) {
      dispatch(
        getUnitYearlyReturnStats({
          groupId: selectedProjectId,
          serviceId: 'fundManagementInvestment',
          unitsHeld: units
        })
      );
    }
  }, [unitsHeld, unitType, selectedProjectId, dispatch]);

  const pctReturn = parseFloat(stats?.pctReturn || '0');
  const profit = formatCurrency(stats?.profit || '0');
  const isPositive = pctReturn >= 0;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        background: blackGrayPalette.cardBg,
        border: blackGrayPalette.border,
        boxShadow: '0 20px 60px rgba(0,0,0,0.9)',
        p: { xs: 3, sm: 4 },
        minHeight: 480,
        position: 'relative',
        overflow: 'hidden',
        backdropFilter: 'blur(16px)'
      }}
    >
      {/* Subtle glow */}
      <Box
        sx={{
          position: 'absolute',
          top: -150,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 450,
          height: 450,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${blackGrayPalette.glow} 0%, transparent 70%)`,
          opacity: 0.25,
          pointerEvents: 'none'
        }}
      />

      {/* Header: Units Held + Input */}
      <Box textAlign="center">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            mb: 1
          }}
        >
          <Typography
            sx={{
              color: '#fff',
              fontSize: { xs: '0.95rem', sm: '1rem' }, // smaller & cleaner
              fontWeight: 600,
              letterSpacing: '0.5px'
            }}
          >
            Units Held
          </Typography>

          <TextField
            value={unitsHeld}
            onChange={(e) => setUnitsHeld(e.target.value.replace(/[^0-9]/g, ''))}
            inputProps={{ inputMode: 'numeric', style: { textAlign: 'center' } }}
            size="small"
            sx={{
              width: { xs: 96, sm: 110 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: '#14161B',
                color: '#fff',
                fontSize: '1.05rem',
                fontWeight: 600,
                height: 38,
                '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
                '&.Mui-focused fieldset': { borderColor: '#4FA3FF', borderWidth: 2 }
              }
            }}
          />
        </Box>

        {/* Helper text */}
        <Typography
          sx={{
            color: 'rgba(255,255,255,0.6)',
            fontSize: '0.78rem',
            maxWidth: 340,
            mx: 'auto',
            mb: 2.5,
            lineHeight: 1.45,
            px: 1
          }}
        >
          Try different unit numbers to test returns based on the average unit price across your
          selected date range.
        </Typography>

        {/* Growth / Income Toggle */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 1,
            maxWidth: 220,
            mx: 'auto',
            mb: 4
          }}
        >
          {(['growth', 'income'] as const).map((t) => (
            <Button
              key={t}
              size="small"
              variant={unitType === t ? 'contained' : 'outlined'}
              onClick={() => setUnitType(t)}
              sx={{
                flex: 1,
                fontSize: '0.78rem',
                py: 0.7,
                borderRadius: 2,
                textTransform: 'capitalize',
                fontWeight: 500,
                backgroundColor: unitType === t ? '#3b82f6' : 'transparent',
                borderColor: unitType === t ? '#3b82f6' : 'rgba(255,255,255,0.2)',
                color: unitType === t ? '#fff' : '#94A3B8',
                '&:hover': {
                  backgroundColor: unitType === t ? '#2563eb' : 'rgba(255,255,255,0.08)'
                }
              }}
            >
              {t === 'growth' ? 'Growth' : 'Income'}
            </Button>
          ))}
        </Box>
      </Box>

      {/* Price Comparison */}
      <Box display="flex" justifyContent="space-between" mb={5} px={{ xs: 1, sm: 2 }}>
        <Box textAlign="center">
          <Typography
            sx={{ color: '#94A3B8', fontSize: '0.65rem', mb: 0.5, letterSpacing: '0.8px' }}
          >
            AVERAGE PRICE OVER PERIOD
          </Typography>
          <Typography sx={{ color: '#E2E8F0', fontSize: '1.22rem', fontWeight: 700 }}>
            ${parseFloat(stats?.avgUnitPriceYear || '0').toFixed(2)}
          </Typography>
        </Box>
        <Box textAlign="center">
          <Typography
            sx={{ color: '#94A3B8', fontSize: '0.65rem', mb: 0.5, letterSpacing: '0.8px' }}
          >
            CURRENT PRICE
          </Typography>
          <Typography sx={{ color: '#60A5FA', fontSize: '1.38rem', fontWeight: 800 }}>
            ${parseFloat(stats?.currentUnitPrice || '0').toFixed(2)}
          </Typography>
        </Box>
      </Box>

      {/* Return Circle */}
      <Box textAlign="center" mb={4}>
        <Typography
          sx={{
            color: '#94A3B8',
            fontSize: '0.62rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '1.4px',
            mb: 1.5
          }}
        >
          Return Over Period
        </Typography>

        <motion.div
          key={pctReturn}
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 20 }}
        >
          <Box
            sx={{
              width: { xs: 136, sm: 168 },
              height: { xs: 136, sm: 168 },
              mx: 'auto',
              borderRadius: '50%',
              background: isPositive
                ? 'radial-gradient(circle, rgba(34,197,94,0.28) 0%, transparent 70%)'
                : 'radial-gradient(circle, rgba(239,68,68,0.28) 0%, transparent 70%)',
              border: `4.5px solid ${isPositive ? '#22c55e' : '#ef4444'}`,
              boxShadow: isPositive
                ? '0 0 55px rgba(34,197,94,0.55)'
                : '0 0 55px rgba(239,68,68,0.55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '1.9rem', sm: '2.5rem' },
                fontWeight: 800,
                color: isPositive ? '#86efac' : '#fca5a5',
                textShadow: `0 0 25px ${isPositive ? 'rgba(34,197,94,0.8)' : 'rgba(239,68,68,0.8)'}`
              }}
            >
              {Math.abs(pctReturn).toFixed(1)}%
            </Typography>
          </Box>
        </motion.div>
      </Box>

      {/* Profit */}
      <Box textAlign="center">
        <Typography
          sx={{
            color: '#94A3B8',
            fontSize: '0.42rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '1.4px',
            mb: 1
          }}
        >
          Profit
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: '2rem', sm: '2.4rem' },
            fontWeight: 800,
            background: isPositive
              ? 'linear-gradient(135deg, #22c55e, #86efac)'
              : 'linear-gradient(135deg, #ef4444, #fca5a5)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
        >
          {profit}
        </Typography>
      </Box>

      {/* Loading */}
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(10,12,15,0.97)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 2.5
          }}
        >
          <CircularProgress size={58} sx={{ color: '#60A5FA' }} />
          <Typography sx={{ color: '#94A3B8', fontSize: '0.85rem' }}>
            Calculating return...
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default function MainDashboard() {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const selectedProjectId = useAppSelector((s) => s.homeTilesServices.selectedProjectId);
  const landingData: UnitData[] = useAppSelector(
    (s) => s.fundManagementInvestment?.landingData || []
  );
  const loading = useAppSelector((s) => s.fundManagementInvestment?.loading);

  const [selectedUnit, setSelectedUnit] = React.useState<'income' | 'growth'>('growth');

  useEffect(() => {
    if (selectedProjectId) {
      dispatch(
        getLandingData({
          groupId: selectedProjectId,
          serviceId: 'fundManagementInvestment',
          isAdmin: true
        })
      );
    }
    return () => dispatch(clearBuySellingUnitsDashboardData());
  }, [selectedProjectId, dispatch]);

  if (loading) {
    return (
      <Container maxWidth={false} sx={{ minHeight: '80vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress size={54} sx={{ color: '#60A5FA' }} />
      </Container>
    );
  }

  const incomeUnitsData = landingData.find((d) => d.unitType === 'incomeUnits') || {};
  const growthUnitsData = landingData.find((d) => d.unitType === 'growthUnits') || {};

  // Safe access using optional chaining + fallback
  const totalNAV =
    parseFloat((incomeUnitsData as any).NAV?.toString() || '0') +
    parseFloat((growthUnitsData as any).NAV?.toString() || '0');

  const totalInvested =
    parseFloat((incomeUnitsData as any).amountInvestedTillDate?.toString() || '0') +
    parseFloat((growthUnitsData as any).amountInvestedTillDate?.toString() || '0');

  const equityGrowth = totalNAV - totalInvested;

  const topStats = [
    {
      icon: <FaWallet />,
      value: formatCurrency(totalNAV),
      label: 'Total NAV',
      subLabel: 'Portfolio Value'
    },
    {
      icon: <FaChartLine />,
      value: formatCurrency(equityGrowth),
      label: 'Equity Growth',
      subLabel: 'Total Profit'
    },
    {
      icon: <FaCoins />,
      value: formatCurrency(totalInvested),
      label: 'Total Invested',
      subLabel: 'All Time'
    }
  ];

  const getUnitStats = (type: 'income' | 'growth') => {
    const data = type === 'income' ? incomeUnitsData : growthUnitsData;
    const nav = parseFloat((data as any).NAV?.toString() || '0');
    const invested = parseFloat((data as any).amountInvestedTillDate?.toString() || '0');
    const profit = nav - invested;
    return [
      { icon: <FaWallet />, value: formatCurrency(nav), label: 'NAV', subLabel: 'Current Value' },
      {
        icon: <FaChartLine />,
        value: formatCurrency(profit),
        label: 'Profit',
        subLabel: 'Realized Gain'
      },
      {
        icon: <FaCoins />,
        value: formatCurrency(invested),
        label: 'Invested',
        subLabel: 'Total Cost'
      }
    ];
  };

  return (
    <Box sx={{ background: blackGrayPalette.background, minHeight: '100vh', pb: 6 }}>
      <Container maxWidth={false} disableGutters sx={{ width: '100%', px: { xs: 1.5, sm: 2.5 } }}>
        <Grid container spacing={2} mb={3}>
          {topStats.map((stat, i) => (
            <Grid item xs={12} sm={4} key={i}>
              <StatCard {...stat} index={i} />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} md={8} order={{ xs: 1, md: 2 }}>
            <AnalyticsPlaceholder />
          </Grid>
          <Grid item xs={12} md={4} order={{ xs: 2, md: 1 }}>
            <ProfitCircle />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} md={5} order={{ xs: 1, md: 2 }}>
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography
                  sx={{
                    color: '#94A3B8',
                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                    fontWeight: 600
                  }}
                >
                  My Portfolio — {selectedUnit === 'income' ? 'Income Units' : 'Growth Units'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {(['income', 'growth'] as const).map((type) => (
                    <Button
                      key={type}
                      size="small"
                      variant={selectedUnit === type ? 'contained' : 'text'}
                      onClick={() => setSelectedUnit(type)}
                      sx={{
                        minWidth: 64,
                        fontSize: '0.65rem',
                        py: 0.4,
                        background: selectedUnit === type ? '#3b82f6' : 'transparent',
                        color: selectedUnit === type ? '#fff' : '#94A3B8'
                      }}
                    >
                      {type === 'income' ? 'Income' : 'Growth'}
                    </Button>
                  ))}
                </Box>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate('/fundManagementInvestment')}
                sx={{
                  fontSize: '0.7rem',
                  py: 0.5,
                  px: 1.5,
                  borderColor: 'rgba(96, 165, 250, 0.3)',
                  color: '#60A5FA'
                }}
              >
                View Details
              </Button>
            </Box>

            <Grid container spacing={1.5}>
              {getUnitStats(selectedUnit).map((stat, i) => (
                <Grid item xs={12} key={i}>
                  <StatCard {...stat} index={i} />
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid item xs={12} md={7} order={{ xs: 2, md: 1 }}>
            <BuySellingUnitsSection onUnitSelect={setSelectedUnit} />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
